/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * EN/FR strings. Mirrors MoveHelper's table (and the iOS Loc table) so
 * translations stay in one conceptual place. Keys are dot-grouped by area;
 * a missing key falls back to EN, then to the key itself.
 */

export type Lang = "en" | "fr";

export const STRINGS: Record<string, { en: string; fr: string }> = {
  "app.name": { en: "MealPlanner", fr: "MealPlanner" },
  "app.tagline": {
    en: "Plan meals, shop once, cook well.",
    fr: "Planifiez les repas, achetez une fois, cuisinez bien.",
  },
  "app.brand": { en: "Sentinelle-IA", fr: "Sentinelle-IA" },

  "nav.section.kitchen": { en: "Kitchen", fr: "Cuisine" },
  // "Today", not "Home". The screen answers what to do today; naming it after
  // its position in a route table told the user nothing.
  "nav.home": { en: "Today", fr: "Aujourd'hui" },
  "nav.plan": { en: "Plan", fr: "Planning" },
  "nav.recipes": { en: "Recipes", fr: "Recettes" },
  "nav.shopping": { en: "Shopping", fr: "Courses" },
  "nav.settings": { en: "Settings", fr: "Réglages" },

  "nav.family": { en: "Household", fr: "Foyer" },

  // ── Account menu ────────────────────────────────────────────────────────
  "nav.account": { en: "Account", fr: "Compte" },
  "nav.appearance": { en: "Appearance", fr: "Apparence" },
  "nav.theme.light": { en: "Light", fr: "Clair" },
  "nav.theme.dark": { en: "Dark", fr: "Sombre" },
  "nav.language": { en: "Language", fr: "Langue" },
  "auth.failed": { en: "Sign-in failed:", fr: "Échec de la connexion :" },
  "family.title": { en: "Family Plan", fr: "Plan familial" },
  "family.subtitle": { en: "Share the plan with your family — same data on every device", fr: "Partagez le planning avec votre famille — les mêmes données partout" },
  "family.loading": { en: "Loading your household…", fr: "Chargement du foyer…" },
  "family.members": { en: "Members", fr: "Membres" },
  "family.member": { en: "Member", fr: "Membre" },
  "family.you": { en: "You", fr: "Vous" },
  "family.youShort": { en: "you", fr: "vous" },
  "family.removeMember": { en: "Remove from the household", fr: "Retirer du foyer" },
  "family.removed": { en: "Removed", fr: "Retiré" },
  "family.yourName": { en: "Your display name", fr: "Votre nom affiché" },
  "family.namePlaceholder": { en: "e.g. Nathan", fr: "ex. Nathan" },
  "family.nameHint": { en: "How the rest of the family sees you on shared lists.", fr: "Comment votre famille vous voit sur les listes partagées." },
  "family.nameSaved": { en: "Name saved", fr: "Nom enregistré" },
  "family.invite": { en: "Invite someone", fr: "Inviter quelqu'un" },
  "family.generateCode": { en: "Generate invite code", fr: "Générer un code" },
  "family.copied": { en: "Copied", fr: "Copié" },
  "family.copyExpires": { en: "Copy · expires in 72h", fr: "Copier · expire dans 72 h" },
  "family.inviteHint": { en: "Shown once and never stored — only a hash is kept, so it can't be recovered.", fr: "Affiché une seule fois et jamais stocké — seul un hachage est conservé." },
  "family.join": { en: "Join another household", fr: "Rejoindre un autre foyer" },
  "family.joinButton": { en: "Join", fr: "Rejoindre" },
  "family.joined": { en: "Joined household", fr: "Foyer rejoint" },
  "family.joinHint": { en: "You'll leave your current household. If you're its last member, its recipes and plans go with it.", fr: "Vous quitterez votre foyer actuel. Si vous en êtes le dernier membre, ses recettes et plannings disparaîtront." },
  "family.eaters": { en: "Who's eating", fr: "Qui mange" },
  "family.manageEaters": { en: "Manage in Settings", fr: "Gérer dans Réglages" },
  "family.noEaters": { en: "No eaters yet — the planner can't size portions or check allergies without them.", fr: "Aucun convive — impossible de calculer les portions ni de vérifier les allergies." },
  "family.addEater": { en: "Add someone", fr: "Ajouter une personne" },
  "family.portion": { en: "portion", fr: "portion" },
  "family.noRestrictions": { en: "No restrictions", fr: "Aucune restriction" },
  "family.enforced": { en: "Enforced across every generated recipe and the whole meal plan:", fr: "Appliqué à chaque recette générée et à tout le planning :" },

  "common.signout": { en: "Sign out", fr: "Déconnexion" },
  "common.signin": { en: "Sign in", fr: "Connexion" },
  "common.save": { en: "Save", fr: "Enregistrer" },
  "common.cancel": { en: "Cancel", fr: "Annuler" },
  "common.add": { en: "Add", fr: "Ajouter" },
  "common.remove": { en: "Remove", fr: "Supprimer" },
  "common.loading": { en: "Loading…", fr: "Chargement…" },
  "common.retry": { en: "Try again", fr: "Réessayer" },
  "common.today": { en: "Today", fr: "Aujourd'hui" },
  "common.tomorrow": { en: "Tomorrow", fr: "Demain" },
  "common.yesterday": { en: "Yesterday", fr: "Hier" },
  "common.servings": { en: "servings", fr: "portions" },
  "common.minutes": { en: "min", fr: "min" },

  "plan.thisWeek": { en: "This week", fr: "Cette semaine" },

  "home.loading": { en: "Getting your day together…", fr: "Préparation de votre journée…" },
  "home.today": { en: "Today", fr: "Aujourd'hui" },
  "home.openPlan": { en: "Open the plan", fr: "Voir le planning" },
  "home.nothingToday": { en: "Nothing planned for today.", fr: "Rien de prévu aujourd'hui." },
  "home.planSomething": { en: "Plan something", fr: "Planifier un repas" },
  "home.weekAhead": { en: "The week ahead", fr: "La semaine à venir" },
  "home.emptyDays": { en: "{n} days empty", fr: "{n} jours vides" },
  "home.ideas": { en: "Ideas for today", fr: "Idées pour aujourd'hui" },
  "home.newIdeas": { en: "New ideas", fr: "Autres idées" },
  "home.ideasAi": { en: "Suggested for your household", fr: "Suggéré pour votre foyer" },
  "home.ideasLocal": { en: "From your own recipes", fr: "Depuis vos recettes" },
  "home.avoiding": { en: "avoiding", fr: "sans" },
  "home.noIdeas": { en: "No suggestions yet — add a few recipes.", fr: "Pas encore de suggestions — ajoutez des recettes." },
  "home.makeRecipe": { en: "Make this a recipe", fr: "En faire une recette" },
  "home.fromLibrary": { en: "Not cooked in a while", fr: "Pas cuisiné depuis un moment" },
  "home.shopping": { en: "Shopping", fr: "Courses" },
  "home.itemsLeft": { en: "{open} of {total} items left", fr: "{open} sur {total} articles restants" },
  "home.startShopping": { en: "Start shopping", fr: "Commencer les courses" },
  "home.generatedAt": { en: "Ideas generated at {time}", fr: "Idées générées à {time}" },
  "plan.print": { en: "Print week", fr: "Imprimer la semaine" },
  "plan.calendar": { en: "Add to calendar", fr: "Ajouter au calendrier" },
  "plan.addMeal": { en: "Add", fr: "Ajouter" },
  "plan.fromRecipe": { en: "From a recipe", fr: "Depuis une recette" },
  "plan.somethingElse": { en: "Something else", fr: "Autre chose" },
  "plan.guests": { en: "Guests for this meal", fr: "Invités pour ce repas" },
  "plan.guestsHowMany": { en: "How many extra people?", fr: "Combien de personnes en plus ?" },
  "plan.guestsFullPortion": {
    en: "They eat a full portion each",
    fr: "Chacun mange une portion complète",
  },
  "plan.guestsChildHint": {
    en: "Useful for children: 3 guests might only be 1.5 servings.",
    fr: "Utile pour les enfants : 3 invités peuvent ne représenter que 1,5 portion.",
  },
  "plan.leftovers": { en: "leftovers", fr: "restes" },
  "plan.nothingPlanned": { en: "(nothing planned)", fr: "(rien de prévu)" },

  "shopping.generate": { en: "Generate from plan", fr: "Générer depuis le planning" },
  "shopping.mode": { en: "Shopping mode", fr: "Mode courses" },
  "shopping.shop": { en: "Shop", fr: "Courses" },
  "shopping.addSomething": { en: "Add something…", fr: "Ajouter un article…" },
  "shopping.ticked": { en: "ticked", fr: "cochés" },
  "shopping.offline": {
    en: "You're offline. Ticking still works — changes sync when you're back.",
    fr: "Vous êtes hors ligne. Les coches fonctionnent toujours — la synchro reprendra au retour du réseau.",
  },
  "shopping.planChanged": {
    en: "The meal plan changed after this list was made.",
    fr: "Le planning a changé après la création de cette liste.",
  },
  "shopping.seeChanges": { en: "See what changed", fr: "Voir les changements" },
  "shopping.skipStaples": { en: "Skip cupboard staples", fr: "Ignorer les essentiels du placard" },
  "shopping.addItem": { en: "Add item", fr: "Ajouter un article" },
  "shopping.itemName": { en: "Item name", fr: "Nom de l'article" },
  "shopping.itemNamePlaceholder": { en: "e.g., Milk, Apples, Bread", fr: "ex: Lait, Pommes, Pain" },
  "shopping.quantity": { en: "Quantity", fr: "Quantité" },
  "shopping.unit": { en: "Unit", fr: "Unité" },
  "shopping.unitPlaceholder": { en: "ct, g, kg, etc.", fr: "pièce, g, kg, etc." },
  "shopping.notes": { en: "Notes", fr: "Remarques" },
  "shopping.subtractPantry": {
    en: "Subtract what's already in the pantry",
    fr: "Déduire ce qui est déjà en réserve",
  },

  "recipes.search": {
    en: "Search — try 'that thing with lemon and orzo'",
    fr: "Rechercher — essayez « le truc au citron et aux orzo »",
  },
  "recipes.generate": { en: "Generate", fr: "Générer" },
  "recipes.scan": { en: "Scan a page", fr: "Scanner une page" },
  "recipes.import": { en: "Import a URL", fr: "Importer une URL" },
  "recipes.neverMade": { en: "Never made", fr: "Jamais préparé" },
  "recipes.cook": { en: "Start cooking", fr: "Commencer à cuisiner" },

  "settings.household": { en: "Household", fr: "Foyer" },
  "settings.planning": { en: "Planning", fr: "Planification" },
  "settings.timezone": { en: "Timezone", fr: "Fuseau horaire" },
  "settings.timezoneHint": {
    en: "An IANA name like America/New_York. Everything the app calls \"today\" is resolved in this zone.",
    fr: "Un nom IANA comme America/New_York. Tout ce que l'application appelle « aujourd'hui » est résolu dans ce fuseau.",
  },
  "settings.weekStart": { en: "Week starts on", fr: "La semaine commence le" },
  "settings.monday": { en: "Monday", fr: "Lundi" },
  "settings.sunday": { en: "Sunday", fr: "Dimanche" },
  "settings.measurements": { en: "Measurements", fr: "Unités" },
  "settings.defaultServings": { en: "Default servings", fr: "Portions par défaut" },
  "settings.whoEating": { en: "Who's eating", fr: "Qui mange" },
  "settings.staples": { en: "Always in the cupboard", fr: "Toujours dans le placard" },
  "settings.invite": { en: "Invite someone", fr: "Inviter quelqu'un" },
  "settings.inviteOnce": {
    en: "This is shown once and can't be recovered. It expires in 72 hours.",
    fr: "Ce code n'est affiché qu'une fois et ne peut pas être récupéré. Il expire dans 72 heures.",
  },
  "settings.circles": { en: "Circles chat", fr: "Discussion Circles" },
  "settings.circlesHint": {
    en: "Enable to create and join private chat rooms with other families.",
    fr: "Activez pour créer et rejoindre des discussions privées avec d'autres familles.",
  },
  "settings.circlesEnable": { en: "Enable Circles chat", fr: "Activer les Circles" },
  "settings.circlesDisable": { en: "Disable Circles chat", fr: "Désactiver les Circles" },

  "circles.loading": { en: "Loading circles…", fr: "Chargement des discussions…" },
  "circles.none": { en: "No circles yet.", fr: "Aucune discussion pour l'instant." },
  "circles.noneHint": {
    en: "Enable Circles in Settings, then create or join a circle to start chatting.",
    fr: "Activez les Circles dans Réglages, puis créez ou rejoignez une discussion pour commencer.",
  },
  "circles.create": { en: "Create circle", fr: "Créer une discussion" },
  "circles.createHint": { en: "Name your circle", fr: "Nommez votre discussion" },
  "circles.join": { en: "Join circle", fr: "Rejoindre une discussion" },
  "circles.joinHint": { en: "Paste an invite code", fr: "Collez un code d'invitation" },
  "circles.leave": { en: "Leave circle", fr: "Quitter la discussion" },
  "circles.leaveConfirm": {
    en: "Are you sure you want to leave this circle? You'll need a new invite code to rejoin.",
    fr: "Voulez-vous vraiment quitter cette discussion ? Vous aurez besoin d'un nouveau code pour la rejoindre.",
  },
  "circles.messages": { en: "Messages", fr: "Messages" },
  "circles.members": { en: "Members", fr: "Membres" },
  "circles.invites": { en: "Invites", fr: "Invitations" },
  "circles.sendMessage": { en: "Send a message…", fr: "Envoyer un message…" },
  "circles.noMessages": { en: "No messages yet.", fr: "Aucun message pour l'instant." },
  "circles.displayName": { en: "Your name in this circle", fr: "Votre nom dans cette discussion" },
  "circles.mute": { en: "Mute notifications", fr: "Activer le mode silencieux" },
  "circles.unmute": { en: "Unmute notifications", fr: "Désactiver le mode silencieux" },
  "circles.subtitle": { en: "Private chat with other families", fr: "Discussions privées avec d'autres familles" },
  "circles.createOrJoin": { en: "Create or join", fr: "Créer ou rejoindre" },
  "circles.created": { en: "Circle created", fr: "Discussion créée" },
  "circles.joined": { en: "Joined circle", fr: "Discussion rejointe" },
  "circles.yourCircles": { en: "Your circles", fr: "Vos discussions" },
  "circles.messageCount": { en: "messages", fr: "messages" },
  "circles.noActivity": { en: "No activity yet", fr: "Aucune activité" },
  "circles.locked": { en: "Locked", fr: "Verrouillée" },
  "circles.infoHint": { en: "Circles are private chat rooms between families. Messages are ephemeral and only visible to circle members.", fr: "Les Circles sont des discussions privées entre familles. Les messages sont éphémères et visibles uniquement par les membres." },
  "common.error": { en: "Something went wrong", fr: "Une erreur est survenue" },

  // Renamed from "Circles", which named the data structure rather than the
  // thing it does. It was also the only nav string that was never translated —
  // a reliable sign that a label has no meaning to translate.
  "nav.circles": { en: "Cooking groups", fr: "Groupes de cuisine" },
  "circles.notFound": { en: "Circle not found", fr: "Discussion introuvable" },
  "circles.left": { en: "Left circle", fr: "Discussion quittée" },
  "circles.youAre": { en: "You are", fr: "Vous êtes" },
  "circles.retentionHint": { en: "Messages older than {days} days are automatically deleted", fr: "Les messages de plus de {days} jours sont automatiquement supprimés" },
  "circles.you": { en: "you", fr: "vous" },
  "circles.messageDeleted": { en: "This message was deleted", fr: "Ce message a été supprimé" },
  "circles.edited": { en: "edited", fr: "modifié" },
  "circles.displayNameSaved": { en: "Display name saved", fr: "Nom affiché enregistré" },
  "circles.typeMessage": { en: "Type your message…", fr: "Saisissez votre message…" },
  "circles.cannotPost": { en: "Circle is locked - cannot post", fr: "Discussion verrouillée - impossible de publier" },
  "circles.suggestedTitleHint": { en: "Suggesting a recipe? (optional)", fr: "Proposez une recette ? (facultatif)" },
  "circles.suggestedTitleDescription": { en: "Name of a dish you're suggesting to make", fr: "Nom d'un plat que vous proposez" },
  "circles.owner": { en: "Owner", fr: "Propriétaire" },
  "circles.lockedInfo": { en: "This circle is locked. Only the owner can post.", fr: "Cette discussion est verrouillée. Seul le propriétaire peut publier." },
  "circles.librarySharing": { en: "Share recipes", fr: "Partager les recettes" },
  "circles.librarySharingHint": { en: "Share all your recipes with this circle continuously", fr: "Partagez toutes vos recettes avec ce cercle en continu" },
  "circles.librarySharingEnabled": { en: "Recipes are being shared with this circle", fr: "Les recettes sont partagées avec ce cercle" },
  "circles.librarySharingDisabled": { en: "Recipe sharing is off", fr: "Le partage de recettes est désactivé" },
  "settings.autoAccept": {
    en: "Add shared recipes automatically",
    fr: "Ajouter automatiquement les recettes partagées",
  },
  "settings.autoAcceptHint": {
    en: "Recipes shared with your circles are copied into your library without asking. Copies are permanent — turning this off later won't remove what already arrived.",
    fr: "Les recettes partagées avec vos cercles sont copiées dans votre bibliothèque sans confirmation. Les copies sont définitives — désactiver cette option ne supprimera pas celles déjà reçues.",
  },
  "circles.addPhoto": { en: "Add photo", fr: "Ajouter une photo" },
  "circles.removePhoto": { en: "Remove photo", fr: "Retirer la photo" },
  "circles.viewPhoto": { en: "View photo", fr: "Voir la photo" },
  "circles.photoFailed": { en: "Couldn't load", fr: "Échec du chargement" },
  "circles.photoProcessing": { en: "Preparing photos…", fr: "Préparation des photos…" },
  "circles.photoLimit": {
    en: "Up to {max} photos, resized before sending",
    fr: "Jusqu'à {max} photos, redimensionnées avant l'envoi",
  },
  "circles.photoTooMany": {
    en: "Only {max} photos per message — the extras were skipped.",
    fr: "Seulement {max} photos par message — les autres ont été ignorées.",
  },
  "circles.availableRecipes": { en: "Available recipes", fr: "Recettes disponibles" },
  "circles.availableRecipesHint": {
    en: "Recipes other families in this circle are sharing. Adding one copies it into your library.",
    fr: "Recettes partagées par les autres familles de ce cercle. En ajouter une la copie dans votre bibliothèque.",
  },
  "circles.librarySharingCount": {
    en: "Sharing {count} recipes with this circle.",
    fr: "{count} recettes partagées avec ce cercle.",
  },
  "circles.acceptRecipe": { en: "Add to my recipes", fr: "Ajouter à mes recettes" },
  "circles.noAvailableRecipes": { en: "No recipes available to share", fr: "Aucune recette disponible à partager" },
  "circles.recipeAccepted": { en: "Recipe added to your library", fr: "Recette ajoutée à votre bibliothèque" },
  "circles.shelfYours": { en: "Yours", fr: "À vous" },
  "circles.shelfOwnOnly": {
    en: "You're sharing {count} recipes with this group — they're on the shelf for everyone else.",
    fr: "Vous partagez {count} recettes avec ce groupe — elles sont disponibles pour tous les autres membres.",
  },
  "circles.tagRecipe": { en: "+ Tag a recipe", fr: "+ Identifier une recette" },
  "circles.tagPickerGroup": { en: "Shared by the group", fr: "Partagées par le groupe" },
  "circles.tagPickerOwn": { en: "Shared by you", fr: "Partagées par vous" },
  "circles.tagEmpty": {
    en: "Share recipes with this group first — Manage sharing",
    fr: "Partagez d'abord des recettes avec ce groupe — Gérer le partage",
  },
  "circles.tagSharingOff": {
    en: "Share your library with this group to tag your own recipes",
    fr: "Partagez votre bibliothèque avec ce groupe pour identifier vos propres recettes",
  },
  "circles.manageSharing": { en: "Manage sharing", fr: "Gérer le partage" },
  "circles.sharedRecipe": { en: "Shared recipe", fr: "Recette partagée" },
  "circles.recipeUnavailable": {
    en: "No longer shared with this group",
    fr: "N'est plus partagée avec ce groupe",
  },
  "circles.manageInvites": { en: "Invites", fr: "Invitations" },
  "circles.manageInvitesHint": { en: "Create invite codes to share this circle with others", fr: "Créer des codes d'invitation pour partager ce cercle avec d'autres" },
  "circles.createInviteCode": { en: "Create invite", fr: "Créer une invitation" },
  "circles.maxUses": { en: "Max uses", fr: "Utilisations max" },
  "circles.inviteHint": { en: "The code will be shown after creation. Share it with other families.", fr: "Le code sera affiché après création. Partagez-le avec d'autres familles." },
  "circles.activeInviteCodes": { en: "Active invite codes", fr: "Codes d'invitation actifs" },
  "circles.noActiveInviteCodes": { en: "No active invite codes", fr: "Aucun code d'invitation actif" },
  "circles.inviteExpires": { en: "Expires", fr: "Expire" },
  "circles.inviteUses": { en: "uses", fr: "utilisations" },
  "circles.statusRevoked": { en: "Revoked", fr: "Révoqué" },
  "circles.inviteCodeCreated": { en: "Invite code created", fr: "Code d'invitation créé" },
  "circles.inviteCodeRevoked": { en: "Invite code revoked", fr: "Code d'invitation révoqué" },
  "common.revoke": { en: "Revoke", fr: "Révoquer" },
  "common.copy": { en: "Copy", fr: "Copier" },
  "common.copied": { en: "Copied!", fr: "Copié !" },
  "common.done": { en: "Done", fr: "Terminé" },
  "common.close": { en: "Close", fr: "Fermer" },
  "common.edit": { en: "Edit", fr: "Modifier" },
  "circles.shareThisCode": { en: "Share this code with other families", fr: "Partagez ce code avec d'autres familles" },
  "circles.codeHint": { en: "Hint", fr: "Indice" },
  "circles.expiresAt": { en: "Expires at", fr: "Expire le" },
  "common.send": { en: "Send", fr: "Envoyer" },

  // Safety copy. Deliberately never promises that a dish is safe.
  "allergen.mayContain": { en: "May contain — unverified", fr: "Peut contenir — non vérifié" },
  "allergen.contains": { en: "Contains", fr: "Contient" },
  "allergen.disclaimer": {
    en: "Ingredient tagging is best-effort. Always read labels — don't rely on this app for a severe allergy.",
    fr: "L'étiquetage des ingrédients est indicatif. Lisez toujours les étiquettes — ne vous fiez pas à cette application en cas d'allergie grave.",
  },

  // ── Nutrition ─────────────────────────────────────────────────────────────
  //
  // "Per serving" is in the TITLE, in both languages, rather than relegated to
  // a subtitle. The panel sits on the same screen as a servings stepper and the
  // figures deliberately do not move when it does — which reads as a bug unless
  // the heading has already said why.
  //
  // Label wording follows UK/EU nutrition-label convention ("of which
  // saturates", "dont acides gras saturés"), because that is the phrasing on
  // the packets in the kitchen this app gets used in.
  "nutrition.title": { en: "Nutrition · per serving", fr: "Nutrition · par portion" },
  "nutrition.kcal": { en: "Calories", fr: "Calories" },
  "nutrition.protein": { en: "Protein", fr: "Protéines" },
  "nutrition.carbs": { en: "Carbohydrate", fr: "Glucides" },
  "nutrition.sugars": { en: "of which sugars", fr: "dont sucres" },
  "nutrition.fat": { en: "Fat", fr: "Lipides" },
  "nutrition.saturates": { en: "of which saturates", fr: "dont acides gras saturés" },
  "nutrition.fibre": { en: "Fibre", fr: "Fibres" },
  "nutrition.sodium": { en: "Sodium", fr: "Sodium" },
  "nutrition.estimated": { en: "Estimated", fr: "Estimation" },
  "nutrition.published": { en: "From the source", fr: "Selon la source" },
  "nutrition.working": { en: "Working out the nutrition…", fr: "Calcul de la nutrition…" },
  "nutrition.calculate": { en: "Estimate nutrition", fr: "Estimer la nutrition" },
  "nutrition.recalculate": { en: "Recalculate", fr: "Recalculer" },
  "nutrition.lowConfidence": {
    en: "Rough — the recipe leaves a lot to taste",
    fr: "Approximatif — la recette laisse beaucoup au goût",
  },
  "nutrition.failed": {
    en: "Couldn't work this one out. Try again in a moment.",
    fr: "Impossible de calculer celle-ci. Réessayez dans un instant.",
  },
  // Says ESTIMATE plainly, and says what it is not. The panel looks very like
  // the one on a packet, which is precisely why it has to say it isn't one.
  "nutrition.disclaimer.estimated": {
    en: "Worked out from the ingredient list, not measured. Treat it as a guide — if a figure matters medically, check it with your GP or dietitian.",
    fr: "Calculé à partir de la liste d'ingrédients, non mesuré. À titre indicatif — si un chiffre a une importance médicale, vérifiez-le auprès de votre médecin ou diététicien.",
  },
  "nutrition.disclaimer.published": {
    en: "Published by the original source, not measured here.",
    fr: "Publié par la source d'origine, non mesuré ici.",
  },
};
